import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { ArticleSchema } from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-2">文章未找到</h2>
          <Link
            to="/"
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 年轻的妈妈`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},育儿,年轻的妈妈`}
        canonicalUrl={`https://young-mama.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleAuthor={article.author}
      />
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        url={`https://young-mama.com/article/${article.id}`}
        image={article.image}
        datePublished={article.publishDate}
        dateModified={article.publishDate}
        authorName={article.author}
      />

      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="py-6 md:py-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium cursor-pointer whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
                <span className="text-foreground-400 text-xs ml-1">{article.authorTitle}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative h-64 md:h-96 rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 年轻的妈妈育儿指南`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-base md:prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-foreground-900
              [&_h3]:font-heading [&_h3]:text-lg md:[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-foreground-800
              [&_p]:text-foreground-700 [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900 [&_strong]:font-semibold
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
              [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Author Card */}
          <div className="flex items-center gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70 mb-12">
            <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-primary-500 text-xl"></i>
            </div>
            <div>
              <p className="font-semibold text-foreground-800 text-sm">{article.author}</p>
              <p className="text-xs text-foreground-500">{article.authorTitle}</p>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-10 pb-16">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative h-40 rounded-lg overflow-hidden mb-3">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{ra.readTime} 分钟阅读</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}